import _ from 'underscore';
import { get, set } from '@bingads-webui/cookie';
import { TargetingScope, TargetingLevel, DeviceName } from './config';
import { TargetingSettingsContext } from './targeting-settings-context';

export function getScope(campaignId, adgroupId) {
  let scope = TargetingScope.Account;

  if (campaignId > 0) {
    if (adgroupId > 0) {
      scope = TargetingScope.AdGroup;
    } else {
      scope = TargetingScope.Campaign;
    }
  }
  return scope;
}

export function getLevel(adgroupId) {
  if (adgroupId > 0) {
    return TargetingLevel.AdGroup;
  }

  return parseInt(get('vnext_settings_level'), 10) || TargetingLevel.Campaign;
}

export function setLevel(level) {
  set('vnext_settings_level', level, undefined, '/');
}

export function getDeviceCriterionModel(model, level) {
  let bulkUpdateModel = null;
  const modelCashbackPercent = _.chain(model)
    .result('CriterionCashback')
    .result('CashbackPercent')
    .value();
  const {
    IsPersonalizedOffersEnabled,
  } = TargetingSettingsContext.pageContext.Permissions;

  const Criterion = ({
    Id, multiplier, deviceName, OSName, Entity,
  }) => ({
    Id: _.isUndefined(Id) ? '' : Id,
    Status: 'Active',
    CriterionType: model.Criterion.Type,
    [Entity]: { Id: model[Entity].Id },
    Criterion: {
      BidAdjustment: multiplier,
      Type: model.Criterion.Type,
      DeviceCriterion: {
        DeviceName: deviceName,
        OSName,
      },
    },
  });

  if (TargetingLevel.Campaign === level) {
    bulkUpdateModel = {
      Name: model.Campaign.Name,
      Id: model.Campaign.Id,
    };
    if (_.has(model, 'OSTargets')) {
      // OS level targets
      _.extend(bulkUpdateModel, {
        CampaignCriterions: _.map(model.OSTargets, ({ Key, Value }) => Criterion({
          Id: Key,
          multiplier: model.CriterionBid.Multiplier,
          deviceName: model.Criterion.DeviceName,
          OSName: Value,
          Entity: 'Campaign',
        })),
      });
    } else {
      _.extend(bulkUpdateModel, {
        CampaignCriterions: _.map(DeviceName, name => Criterion({
          multiplier: 0,
          deviceName: name,
          Entity: 'Campaign',
        })),
      });

      const targetToUpdate = _.find(
        bulkUpdateModel.CampaignCriterions,
        target => _.isEqual(target.Criterion.DeviceCriterion.DeviceName, model.Criterion.DeviceName)
      );
      targetToUpdate.Criterion.BidAdjustment = model.CriterionBid.Multiplier;

      if (IsPersonalizedOffersEnabled) {
        targetToUpdate.Criterion.CashbackPercent = modelCashbackPercent;
      }
    }
  } else {
    bulkUpdateModel = {
      Name: model.AdGroup.Name,
      Id: model.AdGroup.Id,
      Campaign: {
        Id: _.result(model.Campaign, 'Id') || TargetingSettingsContext.state.get('campaign_id'),
      },
      AdGroupCriterions: [],
    };
    if (_.has(model, 'OSTargets')) {
      // OS level targets
      _.extend(bulkUpdateModel, {
        AdGroupCriterions: _.map(model.OSTargets, ({ Key, Value }) => Criterion({
          Id: Key,
          multiplier: model.CriterionBid.Multiplier,
          deviceName: model.Criterion.DeviceName,
          OSName: Value,
          Entity: 'AdGroup',
        })),
      });
    }
  }

  return bulkUpdateModel;
}

export function getCriterionType(view) {
  const scope = view.model.get('scope');
  const level = view.model.get('level');

  return scope === TargetingScope.AdGroup || level === TargetingLevel.AdGroup ? 'AdGroupCriterion' : 'CampaignCriterion';
}

export function getNameForRadiusLocation(lat, lon) {
  const latComputed = Math.round(lat * 100) / 100;
  const longComputed = Math.round(lon * 100) / 100;

  return TargetingSettingsContext.i18n.getString('CustomLocationText')
    .replace('{0}', latComputed)
    .replace('{1}', longComputed);
}

export function getLocationDisplayName(item) {
  if (!_.isUndefined(item.Criterion)) {
    switch (item.Criterion.Type) {
      case 'Location':
        return TargetingSettingsContext.unitMaps.areaTargetNameTypeComboGenerator(
          item.Criterion.Name,
          item.Criterion.LocationType,
          item.Criterion.CountryCode
        ).replace(' (N/A)', '');
      case 'Radius': {
        let name = item.Criterion.Name;
        if (_.isEmpty(name)) {
          name = getNameForRadiusLocation(item.Criterion.Latitude, item.Criterion.Longitude);
        }
        return TargetingSettingsContext.unitMaps.radiusTargetNameTypeComboGenerator(
          item.Criterion.Radius,
          item.Criterion.RadiusUnit,
          name
        );
      }
      default:
        return item.Criterion.Name;
    }
  }

  if (item.IsSegmentRow) {
    return item.SegmentValue;
  }

  if (!_.isUndefined(item.$total)) {
    return item.$total.text;
  }

  return '';
}

export function getDayTimeDisplayName(i18n, item) {
  if (_.isUndefined(item.Criterion)) {
    return item.IsSegmentRow ? item.SegmentValue : item.$total.text;
  }

  const {
    Day, FromHour, FromMinute, ToHour, ToMinute,
  } = item.Criterion;
  const minuteMap = {
    ZERO: 0,
    FIFTEEN: 15,
    THIRTY: 30,
    FORTY_FIVE: 45,
  };
  const dayStr = i18n.getDay(Day.toLowerCase().slice(0, 3));

  const numFromHour = Number(FromHour);
  const numToHour = Number(ToHour);

  const startTime = i18n.formatDate(
    i18n.parseDate(`${numFromHour}:${minuteMap[FromMinute]}`, { raw: 'HH:mm' }),
    { skeleton: 'hhm' }
  );

  const endTime = i18n.formatDate(
    i18n.parseDate(`${numToHour === 24 ? 0 : numToHour}:${minuteMap[ToMinute]}`, { raw: 'HH:mm' }),
    { skeleton: 'hhm' }
  );

  let timeRangeStr = `${startTime} - ${endTime}`;
  if (numFromHour === 0 && FromMinute === 'ZERO' && numToHour === 24 && ToMinute === 'ZERO') {
    timeRangeStr = i18n.getString('All_day');
  }

  return `${dayStr}, ${timeRangeStr}`;
}

export function getDeviceDisplayName(i18n, item) {
  if (_.isUndefined(item.Criterion)) {
    return item.IsSegmentRow ? item.SegmentValue : item.$total.text;
  }

  return i18n.getString(`TargetingSettings_Devices_${item.Criterion.DeviceName}`);
}

export const customValidators = (deviceName, currentCampaign) => {
  const specialBidMultiplier = -100;
  let minBidMultiplier = -100;
  let maxBidMultiplier = 900;
  let isValid = v => (v < 0 && v >= minBidMultiplier) || (v >= 0 && v <= maxBidMultiplier);
  let msgKey = 'TargetingSettings_Device_BidAdj_Error_1';

  if (TargetingSettingsContext.pageContext.Permissions.IsOriginalDeviceTargetingEnabled) {
    minBidMultiplier = -90;
    maxBidMultiplier = 300;

    if (deviceName.DeviceName === 'Smartphones') {
      isValid = v => (v < 0 && (v >= minBidMultiplier || v === specialBidMultiplier))
          || (v >= 0 && v <= maxBidMultiplier);
      msgKey = 'TargetingSettings_Device_BidAdj_Error_2';
    }
  }

  const isCommission = _.chain(currentCampaign).result('BiddingScheme').result('Type').value() === 'Commission';

  if (isCommission) {
    isValid = v => v === 0 || v === -100;
    minBidMultiplier = -100;
    maxBidMultiplier = 0;
    msgKey = 'TargetingSettings_Device_BidAdj_Error_3';
  }

  return i18n => ({
    BidMultiplier: {
      isValid,
      message: () => i18n.getString(msgKey, {
        min: Math.abs(minBidMultiplier),
        max: Math.abs(maxBidMultiplier),
        specialValue: Math.abs(specialBidMultiplier),
      }),
    },
  });
};
